import type { QueryClient } from '@tanstack/react-query';
import type { PowerCurveComparison, Sport, ThresholdKind, ThresholdSport } from '@painpal/contracts';

export type SportFilter = Sport | 'all';
export type ZoneSource = 'power' | 'hr' | 'pace';
export type PowerCurveWindow = PowerCurveComparison['window'];

export const queryKeys = {
  session: ['session'] as const,
  activities: ['activities'] as const,
  activityList: (sport: SportFilter) => ['activities', 'list', sport] as const,
  activity: (id: string) => ['activities', 'detail', id] as const,
  fitness: ['fitness'] as const,
  fitnessRange: (from: string | null, to: string) => ['fitness', from ?? 'all', to] as const,
  // Under the fitness root, so invalidateTraining refreshes the weekly snapshot with the chart.
  week: (start: string, sport: SportFilter = 'all') => ['fitness', 'week', start, sport] as const,
  // Plan rows carry ridden figures, so invalidateTraining refreshes them too.
  plan: ['plan'] as const,
  planRange: (from: string, to: string) => ['plan', from, to] as const,
  // Its own root: a ['plan', …] key would also match reads of plan ranges by prefix.
  recommendations: ['recommendations'] as const,
  thresholds: ['thresholds'] as const,
  thresholdSuggestions: ['thresholds', 'suggestions'] as const,
  thresholdList: (sport: ThresholdSport, kind: ThresholdKind) => ['thresholds', sport, kind] as const,
  zones: ['zones'] as const,
  weeklyZones: (from: string | null, to: string, source: ZoneSource, sport: SportFilter = 'all') =>
    ['zones', 'weekly', from ?? 'all', to, source, sport] as const,
  powerCurves: ['power-curve'] as const,
  powerCurve: (id: string, window: PowerCurveWindow) => ['power-curve', id, window] as const,
  settings: ['settings'] as const,
  strava: ['strava-status'] as const,
  account: ['account'] as const,
  upload: (key: string) => ['upload', key] as const,
  streams: (activityId: string) => ['streams', activityId] as const,
};

// Every mutation that starts a recalculation, keyed by the sport and threshold kind it changes so the banner can word itself.
export const mutationKeys = {
  thresholdChanges: ['threshold-change'] as const,
  thresholdChange: (sport: ThresholdSport, kind: ThresholdKind) => ['threshold-change', sport, kind] as const,
};

// Every query derived from rides and thresholds. Over-invalidating costs a refetch; missing one shows stale numbers.
const TRAINING_ROOTS = [queryKeys.thresholds, queryKeys.zones, queryKeys.fitness, queryKeys.activities, queryKeys.powerCurves, queryKeys.plan, queryKeys.recommendations];

export function invalidateTraining(queryClient: QueryClient): Promise<unknown> {
  return Promise.all(TRAINING_ROOTS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}
